import type { Webhook, Endpoint } from '@square/ignition';

/**
 * Takes a map of api categories to webhooks, and sorts the map keys as well as the webhooks in each bucket.
 */
function getSortedCategoriesWebhooksMap(
  categoriesWebhooksMap: Map<string, Webhook[]>
) {
  const sortedCategoriesWebhooksMap = new Map();
  [...categoriesWebhooksMap.keys()].sort().forEach((api) => {
    const sortedWebhooks = categoriesWebhooksMap.get(api)?.sort((a, b) => {
      const rootNameA = a.name;
      const rootNameB = b.name;

      if (rootNameA === rootNameB) {
        return a.isV1 ? 1 : -1;
      } else {
        return rootNameB.toLowerCase() < rootNameA.toLowerCase() ? 1 : -1;
      }
    });

    sortedCategoriesWebhooksMap.set(api, sortedWebhooks);
  });
  return sortedCategoriesWebhooksMap;
}

/**
 * Adds the given webhook to the map indexed by the api category it's in.
 */
function addWebhookByApiCategoryToMap(
  map: Map<string, Webhook[]>,
  webhook: Webhook
) {
  const apiCategory = webhook?.api?.name || '';
  let webhookList = map.get(apiCategory);

  if (!webhookList) {
    webhookList = [];
    map.set(apiCategory, webhookList);
  }

  webhookList.push(webhook);
  return map;
}

/**
 * Takes a map of api categories to endpoints, and sorts the map keys as well as the endpoints in each bucket.
 */
function getSortedCategoriesEndpointsMap(
  categoriesEndpointsMap: Map<string, Endpoint[]>
) {
  const sortedCategoriesEndpointsMap = new Map();
  [...categoriesEndpointsMap.keys()].sort().forEach((endpoint) => {
    const sortedEndpoints = categoriesEndpointsMap
      .get(endpoint)
      ?.sort((a, b) => {
        const rootNameA = a.name;
        const rootNameB = b.name;

        if (rootNameA === rootNameB) {
          return a.isV1 ? 1 : -1;
        } else {
          return rootNameB.toLowerCase() < rootNameA.toLowerCase() ? 1 : -1;
        }
      });

    sortedCategoriesEndpointsMap.set(endpoint, sortedEndpoints);
  });
  return sortedCategoriesEndpointsMap;
}

/**
 * Adds the given webhook to the map indexed by the api category it's in.
 */
function addEndpointByApiCategoryToMap(
  map: Map<string, Endpoint[]>,
  endpoint: Endpoint
) {
  const apiCategory = endpoint?.api?.name || '';
  let endpointList = map.get(apiCategory);

  if (!endpointList) {
    endpointList = [];
    map.set(apiCategory, endpointList);
  }

  endpointList.push(endpoint);
  return map;
}

export {
  addWebhookByApiCategoryToMap,
  getSortedCategoriesWebhooksMap,
  addEndpointByApiCategoryToMap,
  getSortedCategoriesEndpointsMap,
};
